import type { Dispatch, SetStateAction } from 'react';
import { Box, Checkbox, DropdownFilter, FormGroup } from '@ksyos/design-system';
import { useCallback } from 'react';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type { Profession } from '@common/enums/profession';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';
import type { OutpatientClinicTaskListFilter } from '../../types/outpatientClinicTaskListFilter';

export type PerformerFilterProps = {
    filter: OutpatientClinicTaskListFilter;
    localPractitioners: OutpatientClinicTaskListFilter['performerPractitioner'];
    localProfessions: OutpatientClinicTaskListFilter['performerPractitionerProfession'];
    practitioners: NonNullable<OutpatientClinicTask['performerPractitioner']>[];
    professions: NonNullable<OutpatientClinicTask['performerPractitionerProfession']>[];
    setFilter: (filter: OutpatientClinicTaskListFilter) => void;
    setLocalPractitioners: Dispatch<SetStateAction<OutpatientClinicTaskListFilter['performerPractitioner']>>;
    setLocalProfessions: Dispatch<SetStateAction<OutpatientClinicTaskListFilter['performerPractitionerProfession']>>;
};

export function PerformerFilter({
    filter,
    setFilter,
    practitioners,
    professions,
    localProfessions,
    setLocalProfessions,
    localPractitioners,
    setLocalPractitioners,
}: PerformerFilterProps) {
    const translate = getSelectedLanguage();

    const getFilteredPerformerCount = useCallback(() => {
        return (
            (filter.performerPractitionerProfession && filter.performerPractitionerProfession.length > 0
                ? filter.performerPractitionerProfession.length
                : 0) +
            (filter.performerPractitioner
                ? Array.isArray(filter.performerPractitioner)
                    ? filter.performerPractitioner.length
                    : 1
                : 0)
        );
    }, [filter.performerPractitionerProfession, filter.performerPractitioner]);

    const getLocalPerformerCount = useCallback(
        (
            item:
                | OutpatientClinicTaskListFilter['performerPractitioner']
                | OutpatientClinicTaskListFilter['performerPractitionerProfession']
        ): string => {
            if (Array.isArray(item) && item.length > 0) {
                return ` (${item.length})`;
            } else if (item) {
                return ' (1)';
            }

            return '';
        },
        []
    );

    const addProfession = useCallback(
        (professionToAdd: Profession) => {
            setLocalProfessions((prev) => {
                if (prev === undefined) {
                    return professionToAdd;
                }

                if (!Array.isArray(prev)) {
                    return prev === professionToAdd ? prev : [prev, professionToAdd];
                }

                return prev.includes(professionToAdd) ? prev : [...prev, professionToAdd];
            });
        },
        [setLocalProfessions]
    );

    const removeProfession = useCallback(
        (professionToRemove: Profession) => {
            setLocalProfessions((prev) => {
                if (!Array.isArray(prev)) {
                    return prev === professionToRemove ? undefined : prev;
                }
                const filtered = prev.filter((prevProfession) => prevProfession !== professionToRemove);

                if (filtered.length === 1) {
                    return filtered[0];
                }
                if (filtered.length > 0) {
                    return filtered;
                }

                return undefined;
            });
        },
        [setLocalProfessions]
    );

    const addPractitioner = useCallback(
        (practitionerId: string) => {
            setLocalPractitioners((prev) => {
                if (!prev) {
                    return { id: practitionerId };
                }

                if (!Array.isArray(prev)) {
                    return prev.id === practitionerId ? prev : [prev, { id: practitionerId }];
                }

                const practitionerExists = prev.some((prevPractitioner) => prevPractitioner.id === practitionerId);

                return practitionerExists ? prev : [...prev, { id: practitionerId }];
            });
        },
        [setLocalPractitioners]
    );

    const removePractitioner = useCallback(
        (practitionerId: string) => {
            setLocalPractitioners((prev) => {
                if (!prev) {
                    return prev;
                }

                if (!Array.isArray(prev)) {
                    return prev.id === practitionerId ? undefined : prev;
                }

                const filtered = prev.filter((prevPractitioner) => prevPractitioner.id !== practitionerId);

                return filtered.length === 1 ? filtered[0] : filtered.length > 0 ? filtered : undefined;
            });
        },
        [setLocalPractitioners]
    );

    const handleReset = useCallback(() => {
        setLocalProfessions(undefined);
        setLocalPractitioners(undefined);
        setFilter({
            ...filter,
            performerPractitioner: undefined,
            performerPractitionerProfession: undefined,
        });
    }, [filter, setFilter, setLocalPractitioners, setLocalProfessions]);

    const handleSubmit = useCallback(() => {
        setFilter({
            ...filter,
            performerPractitioner: localPractitioners,
            performerPractitionerProfession: localProfessions,
        });
    }, [filter, localPractitioners, localProfessions, setFilter]);

    return (
        <FormGroup label={translate.procedure.performer}>
            <DropdownFilter
                count={getFilteredPerformerCount()}
                label={translate.common.filter}
                noPadding={true}
                onReset={handleReset}
                onSubmit={handleSubmit}
                width="full"
            >
                <DropdownFilter.Accordion type="multiple">
                    <ProfessionAccordionItem
                        addProfession={addProfession}
                        getLocalPerformerCount={getLocalPerformerCount}
                        localProfessions={localProfessions}
                        professions={professions}
                        removeProfession={removeProfession}
                    />
                    <PractitionerAccordionItem
                        addPractitioner={addPractitioner}
                        getLocalPerformerCount={getLocalPerformerCount}
                        localPractitioners={localPractitioners}
                        practitioners={practitioners}
                        removePractitioner={removePractitioner}
                    />
                </DropdownFilter.Accordion>
            </DropdownFilter>
        </FormGroup>
    );
}

type ProfessionAccordionItemProps = {
    addProfession: (profession: Profession) => void;
    getLocalPerformerCount: (
        item:
            | OutpatientClinicTaskListFilter['performerPractitioner']
            | OutpatientClinicTaskListFilter['performerPractitionerProfession']
    ) => string;
    localProfessions: OutpatientClinicTaskListFilter['performerPractitionerProfession'];
    professions: NonNullable<OutpatientClinicTask['performerPractitionerProfession']>[];
    removeProfession: (profession: Profession) => void;
};

function ProfessionAccordionItem({
    professions,
    localProfessions,
    getLocalPerformerCount,
    addProfession,
    removeProfession,
}: ProfessionAccordionItemProps) {
    const translate = getSelectedLanguage();

    return (
        <DropdownFilter.AccordionItem
            label={`${translate.common.profession} ${getLocalPerformerCount(localProfessions)}`}
            value="professions"
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={2}
            >
                {professions.map((profession) => (
                    <Checkbox
                        checked={
                            Array.isArray(localProfessions)
                                ? localProfessions.includes(profession as Profession)
                                : localProfessions === profession
                        }
                        key={profession}
                        label={
                            translate.processActorProfession[
                                profession as keyof typeof translate.processActorProfession
                            ] || profession
                        }
                        onChange={(event) => {
                            if (event.target.checked) {
                                addProfession(profession as Profession);
                            } else {
                                removeProfession(profession as Profession);
                            }
                        }}
                    />
                ))}
            </Box>
        </DropdownFilter.AccordionItem>
    );
}

type PractitionerAccordionItemProps = {
    addPractitioner: (practitionerId: string) => void;
    getLocalPerformerCount: (
        item:
            | OutpatientClinicTaskListFilter['performerPractitioner']
            | OutpatientClinicTaskListFilter['performerPractitionerProfession']
    ) => string;
    localPractitioners: OutpatientClinicTaskListFilter['performerPractitioner'];
    practitioners: NonNullable<OutpatientClinicTask['performerPractitioner']>[];
    removePractitioner: (practitionerId: string) => void;
};

function PractitionerAccordionItem({
    practitioners,
    localPractitioners,
    getLocalPerformerCount,
    addPractitioner,
    removePractitioner,
}: PractitionerAccordionItemProps) {
    const translate = getSelectedLanguage();

    return (
        <DropdownFilter.AccordionItem
            label={`${translate.practitioner.practitioner} ${getLocalPerformerCount(localPractitioners)}`}
            value="practitioners"
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={3}
            >
                {practitioners.map((practitioner) => (
                    <Checkbox
                        checked={
                            Array.isArray(localPractitioners)
                                ? localPractitioners.some((prevPractitioner) => prevPractitioner.id === practitioner.id)
                                : localPractitioners?.id === practitioner.id
                        }
                        key={practitioner.id}
                        label={getDisplayName(practitioner)}
                        onChange={(event) => {
                            if (event.target.checked) {
                                addPractitioner(practitioner.id);
                            } else {
                                removePractitioner(practitioner.id);
                            }
                        }}
                    />
                ))}
            </Box>
        </DropdownFilter.AccordionItem>
    );
}
